/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Autocomplete, Box, TextField } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import PostService from "../../../services/postService";
import { returnStringByLang } from "../../../utils";

const cacheRtl = createCache({ key: 'muirtl', stylisPlugins: [rtlPlugin] });
const cacheLtr = createCache({ key: 'ltr' });

const PostStatusDropdown = ({
    helperText,
    error,
    setFormValues,
    isFilter,
    selectedPostStatus,
    setSelectedPostStatus,
    setInitialPostStatus,
    byPassInitial
}) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();

    const [postStatus, setPostStatus] = useState([]);

    useEffect(() => {
        const fetchPostStatus = async () => {
            try {
                const data = await PostService.fetchAllPostStatuses();
                setPostStatus(data);

                if (!selectedPostStatus && !isFilter && !byPassInitial) {
                    const isInitial = data.find((status) => status.isInitial);
                    setSelectedPostStatus(isInitial);
                    setInitialPostStatus && setInitialPostStatus(isInitial);
                    setFormValues &&
                        setFormValues((prevValues) => ({
                            ...prevValues,
                            postStatusId: isInitial.id || null,
                        }));
                }
            } catch (error) {
                console.error("Error fetching post status:", error);
            }
        }
        fetchPostStatus();
    }, []);

    return (
        <CacheProvider value={i18n.language === "en" ? cacheLtr : cacheRtl}>
            <Autocomplete
                options={postStatus}
                getOptionLabel={(option) => returnStringByLang(option, i18n.language) || ""}
                value={selectedPostStatus}
                onChange={(event, newValue) => {
                    setSelectedPostStatus(newValue);
                    setFormValues &&
                        setFormValues((prevValues) => ({
                            ...prevValues,
                            postStatusId: newValue ? newValue.id : null,
                        }));
                }}
                renderOption={(props, option) => (
                    <li {...props} key={option.id} style={{ color: option.color, fontWeight: 'bold' }}>
                        {returnStringByLang(option, i18n.language)}
                    </li>
                )}
                renderInput={(params) => (
                    <Box>
                        {!isFilter && (
                            <p
                                style={{
                                    color: theme.palette.text.primary,
                                    fontSize: 13,
                                    marginTop: 24,
                                    marginBottom: 4,
                                }}
                            >
                                {t("postStatus")}
                            </p>
                        )}

                        <TextField
                            {...params}
                            variant={isFilter ? "standard" : "outlined"}
                            fullWidth
                            placeholder={isFilter ? t("postStatus") : t("choosePostStatus")}
                            error={!!error}
                            helperText={helperText}
                            InputProps={{
                                ...params.InputProps,
                                ...(isFilter ? { disableUnderline: true } : {}),
                                endAdornment: (
                                    <>
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                                style: {
                                    backgroundColor: isFilter
                                        ? "transparent"
                                        : theme.palette.customGrey.insideInput,
                                    padding: "6px 8px",
                                    cursor: "pointer",
                                },
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: theme.palette.customGrey.borderInput,
                                    },
                                    "&:hover fieldset": {
                                        borderColor: theme.palette.customGrey.borderInput,
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: theme.palette.primary.main,
                                    },
                                },
                                "& .MuiInputBase-input": {
                                    color: theme.palette.text.primary,
                                    padding: "6px 8px",
                                    "&::placeholder": {
                                        fontSize: "14px",
                                        color: theme.palette.text.secondary,
                                        opacity: 1,
                                    },
                                },
                            }}
                        />
                    </Box>
                )}
            />
        </CacheProvider>
    );
};

export default PostStatusDropdown;
